// Phase 83i — Avatar collection (DiceBear avataaars)
// Goal: visual consistency across the app. Every athlete-facing list / card uses
// the SAME visual avatar regardless of whether the user uploaded one. The real
// uploaded avatar is reserved for the Profile detail screen ONLY.
//
// Phase 85.A.206 — Dùng BACKEND_URL (smart-fallback) thay vì process.env.
import { BACKEND_URL } from "./api";

const POOL_SEEDS = [
  "Aurora", "Blaze", "Cosmo", "Drift", "Echo", "Falcon",
  "Glacier", "Halo", "Indigo", "Juno", "Koi", "Luna",
  "Maverick", "Nimbus", "Onyx", "Phoenix", "Quartz", "Riven",
  "Solstice", "Tide", "Umbra", "Vega", "Whisper", "Xeno",
  "Zephyr", "Atlas", "Briar", "Cinder", "Delta", "Ember",
  "Frost", "Gale", "Helix", "Iris", "Jade", "Karma",
];

export const COLLECTION_AVATARS = POOL_SEEDS.map((seed) => ({
  seed,
  url: "/avatar-fallback.svg",
}));

// Fast non-cryptographic hash (FNV-1a 32-bit) — deterministic across JS engines.
function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * Resolve the canonical avatar URL for any user/team-like entity.
 *
 * Phase 84.M.87 — Behaviour change: PRIORITIZE uploaded `avatar_url` over
 * collection seeds. User request: "nếu avatar của VĐV đã upload thì yêu cầu
 * ưu tiên hiển thị avatar này trước chứ ko hiển thị trong bộ avatar có sẵn".
 *
 * Pass `{ preferCollection: true }` to opt-out (eg. for visual identity
 * grids where consistency matters more than the actual face).
 *
 * Priority:
 *   1. input.avatar_url            (uploaded photo — UI WANTS THIS FIRST)
 *   2. input.avatar                (sometimes already-resolved by parent)
 *   3. input.avatar_collection_url (direct, if backend pre-resolved)
 *   4. input.avatar_collection_seed (turn into DiceBear URL)
 *   5. deterministic from id / username / name
 *   6. ABSOLUTE final fallback: POOL_SEEDS[0]
 */
export function resolveCollectionAvatar(input, opts = {}) {
  if (!input) return COLLECTION_AVATARS[0].url;

  // String shortcut — treat as id
  if (typeof input === "string") {
    return seedToUrl(deriveSeedFromKey(input));
  }

  const preferCollection = opts && opts.preferCollection === true;

  // Phase 84.M.87 — Ưu tiên avatar đã upload thật trừ khi caller opt-out.
  if (!preferCollection) {
    // v16.63 — Ưu tiên thumbnail 128px (nhẹ ~5-15KB) cho list/card public.
    if (input.avatar_thumb_url) {
      return resolveAvatarUrlAbs(input.avatar_thumb_url);
    }
    if (input.avatar_url) {
      return resolveAvatarUrlAbs(input.avatar_url);
    }
    if (input.avatar) {
      return resolveAvatarUrlAbs(input.avatar);
    }
  }

  if (input.avatar_collection_url) return input.avatar_collection_url;
  if (input.avatar_collection_seed) return seedToUrl(input.avatar_collection_seed);

  const key =
    input.user_id ||
    input.id ||
    input.username ||
    input.email ||
    input.name ||
    input.full_name ||
    "default";
  return seedToUrl(deriveSeedFromKey(String(key)));
}

// Phase 84.M.87 — Normalize relative avatar paths against backend URL.
// Tránh phải import absUrl từ userDisplay (gây circular import potential).
// Phase 85.A.206 — Dùng BACKEND_URL (smart-fallback) từ api.js.
function resolveAvatarUrlAbs(u) {
  if (!u || typeof u !== "string") return u;
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  return `${BACKEND_URL}${u}`;
}

/**
 * Variant that ALWAYS returns the collection avatar (never the uploaded photo).
 * Use this for lists/cards where we want consistent visual identity.
 */
export function getCollectionAvatar(userOrId) {
  return resolveCollectionAvatar(userOrId, { preferCollection: true });
}

/**
 * Variant for the Profile detail header. Returns the uploaded photo when
 * available; otherwise falls back to the collection avatar.
 */
export function getProfileAvatar(user) {
  if (!user) return COLLECTION_AVATARS[0].url;
  if (user.avatar_url) return user.avatar_url;
  return resolveCollectionAvatar(user);
}

/**
 * 2026-07-31 P0 — Fallback chain khi <img avatar> lỗi (403/404 từ DO Spaces).
 * Dùng trong onError:
 *   onError={(e) => { e.currentTarget.src = getAvatarFallback(user, step); step += 1; }}
 * Chuỗi: avatar_url → avatar_thumb_url → collection seed → default seed.
 */
export function getAvatarFallback(user, step = 0) {
  if (!user) return COLLECTION_AVATARS[0].url;
  const chain = [];
  if (user.avatar_thumb_url) chain.push(resolveAvatarUrlAbs(user.avatar_thumb_url));
  chain.push(resolveCollectionAvatar(user, { preferCollection: true }));
  chain.push(COLLECTION_AVATARS[0].url);
  return chain[Math.min(step, chain.length - 1)];
}

export function deriveSeedFromKey(key) {
  const idx = fnv1a(String(key)) % POOL_SEEDS.length;
  return POOL_SEEDS[idx];
}

export function seedToUrl(seed) {
  return "/avatar-fallback.svg";
}

/** True when `seed` is one of the curated pool seeds. */
export function isPoolSeed(seed) {
  return POOL_SEEDS.includes(seed);
}
